import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Bell,
  BarChart3,
  BookOpenText,
  CalendarCheck,
  CalendarRange,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  Home,
  LogOut,
  Menu,
  PackageCheck,
  Search,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { StoreLocation, StoreUser } from "./api";
import { rememberStoreLocationId } from "./storeLocationPreference";
import { defaultLogoUrl } from "@/utils/branding";

type StoreLayoutProps = {
  children: ReactNode;
  title: string;
  user: StoreUser | null;
  onLogout: () => void;
  currentLocationId?: string | null;
  onLocationChange?: (locationId: string) => void;
};

type StoreNavItem = {
  label: string;
  href: string;
  icon: ReactNode;
  permissionKey: string;
};

const navItems: StoreNavItem[] = [
  { label: "門市工作總覽", href: "/store/dashboard", icon: <Home size={18} />, permissionKey: "store-dashboard" },
  { label: "預約管理", href: "/store/reservations", icon: <CalendarCheck size={18} />, permissionKey: "store-reservations" },
  { label: "客戶查詢", href: "/store/customers", icon: <Users size={18} />, permissionKey: "store-customers" },
  { label: "服務紀錄", href: "/store/service-records", icon: <ClipboardList size={18} />, permissionKey: "store-service-records" },
  { label: "門市訂單", href: "/store/orders", icon: <PackageCheck size={18} />, permissionKey: "store-orders" },
  { label: "回訪待辦", href: "/store/follow-ups", icon: <Bell size={18} />, permissionKey: "store-follow-ups" },
  { label: "公告 SOP", href: "/store/announcements", icon: <BookOpenText size={18} />, permissionKey: "store-announcements" },
  { label: "門市日曆", href: "/store/google-calendar", icon: <CalendarDays size={18} />, permissionKey: "store-google-calendar" },
  { label: "門市報表", href: "/store/reports", icon: <BarChart3 size={18} />, permissionKey: "store-reports" },
  { label: "門市預約時段", href: "/store/schedule", icon: <CalendarRange size={18} />, permissionKey: "store-schedule-adjust" },
];

function formatLocation(location?: StoreLocation | null) {
  return location?.Name || "未指定門市";
}

export default function StoreLayout({ children, currentLocationId, onLocationChange, onLogout, title, user }: StoreLayoutProps) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [locationMenuOpen, setLocationMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const locations = useMemo(() => user?.Locations || [], [user]);
  const activeLocation = useMemo(() => {
    if (currentLocationId) {
      return locations.find((item) => item.LocationId === currentLocationId) || null;
    }
    return locations.find((item) => item.IsPrimary) || locations[0] || null;
  }, [currentLocationId, locations]);

  function isActive(href: string) {
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  }

  const visibleNavItems = useMemo(() => {
    if (!user) {
      return navItems;
    }
    const permissions = new Set(user.Permissions || []);
    return navItems.filter((item) => permissions.has(item.permissionKey));
  }, [user]);

  const sidebar = (
    <aside className="flex h-full w-[292px] flex-col border-r border-[#dfe6e1] bg-white">
      <div className="flex h-[78px] items-center justify-between border-b border-[#edf0ee] px-4">
        <Link className="flex min-w-0 items-center gap-2" to="/store/dashboard">
          <img className="h-12 w-12 shrink-0 object-contain" src={defaultLogoUrl} alt="富聆聽健康" />
          <div className="min-w-0">
            <div className="truncate text-lg font-extrabold text-[#315746]">富聆聽<span className="text-[#d4a574]">健康</span></div>
            <div className="truncate text-xs font-bold tracking-[.16em] text-[#8b9a92]">STORE PORTAL</div>
          </div>
        </Link>
        <button
          aria-label="關閉側邊選單"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
          type="button"
        >
          <X size={18} />
        </button>
      </div>

      <div className="border-b border-[#edf0ee] px-4 py-4">
        <div className="text-xs font-bold text-[#7b877f]">目前門市</div>
        <div className="relative mt-2">
          <button
            className="flex h-11 w-full items-center justify-between gap-2 rounded-md border border-[#cfd8d2] bg-[#f7faf8] px-3 text-left text-sm font-bold text-[#203d32]"
            onClick={() => setLocationMenuOpen((current) => !current)}
            type="button"
          >
            <span className="truncate">{formatLocation(activeLocation)}</span>
            <ChevronDown className={`shrink-0 transition ${locationMenuOpen ? "rotate-180" : ""}`} size={16} />
          </button>
          {locationMenuOpen && locations.length > 0 && (
            <div className="absolute left-0 right-0 top-[48px] z-30 rounded-md border border-[#dfe6e1] bg-white p-1 shadow-[0_14px_36px_rgba(32,61,50,.14)]">
              {locations.map((item) => (
                <button
                  className={`block w-full rounded px-3 py-2 text-left text-sm font-bold transition ${
                    item.LocationId === activeLocation?.LocationId ? "bg-[#e9f4ec] text-[#245438]" : "text-[#59665f] hover:bg-[#f7faf8]"
                  }`}
                  key={item.LocationId}
                  onClick={() => {
                    rememberStoreLocationId(item.LocationId);
                    onLocationChange?.(item.LocationId);
                    setLocationMenuOpen(false);
                  }}
                  type="button"
                >
                  {item.Name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {visibleNavItems.map((item) => (
          <Link
            className={`flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-sm font-bold transition ${
              isActive(item.href) ? "bg-[#e9f4ec] text-[#245438]" : "text-[#59665f] hover:bg-[#f0f5f2] hover:text-[#315746]"
            }`}
            key={item.href}
            to={item.href}
          >
            <span className="shrink-0 text-[#315746]">{item.icon}</span>
            {item.label}
          </Link>
        ))}
      </nav>

      <div className="border-t border-[#edf0ee] p-3">
        <button
          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white font-bold text-[#315746] transition hover:bg-[#f0f5f2]"
          onClick={onLogout}
          type="button"
        >
          <LogOut size={18} />
          登出
        </button>
      </div>
    </aside>
  );

  return (
    <main className="portal-rwd min-h-screen bg-[#f6f7f5] text-[#24352d]">
      {mobileMenuOpen && (
        <button
          aria-label="關閉側邊選單遮罩"
          className="fixed inset-0 z-30 bg-[#10251c]/35 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
          type="button"
        />
      )}

      <div className={`fixed inset-y-0 left-0 z-40 transition-transform lg:translate-x-0 ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"}`}>
        {sidebar}
      </div>

      <div className="min-h-screen lg:pl-[292px]">
        <header className="sticky top-0 z-20 border-b border-[#dfe6e1] bg-white/95 backdrop-blur">
          <div className="flex min-h-[60px] items-center justify-between gap-3 px-4 py-2 md:min-h-[68px] md:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <button
                aria-label="開啟側邊選單"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] bg-white text-[#315746] lg:hidden"
                onClick={() => setMobileMenuOpen(true)}
                type="button"
              >
                <Menu size={18} />
              </button>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 text-xs font-bold text-[#7b877f]">
                  <Home size={12} />
                  門市端
                </div>
                <h1 className="truncate text-lg font-extrabold text-[#203d32] md:text-xl">{title}</h1>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <Link
                className="hidden h-10 items-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2] sm:inline-flex"
                to="/store/customers"
              >
                <Search size={16} />
                客戶搜尋
              </Link>
              <div className="hidden items-center gap-2 rounded-md bg-[#f0f5f2] px-3 py-2 text-sm font-bold text-[#315746] sm:flex">
                <UserRound size={16} />
                <span>{user?.DisplayName || "門市同仁"}</span>
              </div>
            </div>
          </div>
        </header>

        <section className="mx-auto w-full max-w-[1920px] px-3 py-4 sm:px-4 md:px-5 md:py-5 2xl:px-8">{children}</section>
      </div>
    </main>
  );
}
